import useObservableRefresh from '@eduzz/houston-hooks/useObservableRefresh';
import Typography from '@eduzz/houston-ui/Typography';
import Grid from '@material-ui/core/Grid';
import ColoredFormattedNumber from 'components/ColoredFormattedNumber';
import Spin from 'components/Spin';
import React, { useMemo } from 'react';
import { FormattedNumber } from 'react-intl';
import Error from 'v2/components/Shared/Error';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import ValueCard from 'v2/components/Shared/ValueCard';
import salesService from 'v2/services/sales/general';

import type { ICardProps } from 'v2/components/Shared/ValueCard';
import type { ISalesDetails } from 'v2/interfaces/sales';

import { useStyles } from './styles';

const Statics = () => {
  const classes = useStyles();
  const [details, hasDetailsError, isLoadingDetails, handleRetry] = useObservableRefresh<ISalesDetails>(() => salesService.getSalesDetails(), []);

  const incomeCards: ICardProps[] = useMemo(
    () => [
      {
        title: 'Total Vendas',
        total: (
          <PermissionHide inverse action='myeduzz:minhas-vendas:ocultar-valores' fallback={<span>oculto</span>}>
            <ColoredFormattedNumber value={details?.income?.total_sales} format='BRL' />
          </PermissionHide>
        ),

        helpText:
          'Aqui você encontra a soma de todas as suas vendas já confirmadas, inclusive as que estão aguardando reembolso ou já foram reembolsadas.',
      },
      {
        title: 'Ganho Líquido',
        total: (
          <PermissionHide inverse action='myeduzz:minhas-vendas:ocultar-valores' fallback={<span>oculto</span>}>
            <ColoredFormattedNumber value={details?.income?.net_gain} format='BRL' />
          </PermissionHide>
        ),
        helpText:
          'Nos Ganhos Líquidos você pode conferir o cálculo do seu lucro total, com todas as taxas, comissões e reembolsos aplicados até o momento.',
      },
      {
        title: 'Reembolsos',
        total: (
          <PermissionHide inverse action='myeduzz:minhas-vendas:ocultar-valores' fallback={<span>oculto</span>}>
            <FormattedNumber value={details?.income?.total_refunds} format='BRL' />
          </PermissionHide>
        ),
        totalColor: '#dc3f53',
        helpText: 'Essa é a soma dos valores de todas as Faturas Reembolsadas até o momento, inclusive os valores de reembolsos parciais.',
      },
    ],
    [details]
  );

  const invoiceCards: ICardProps[] = useMemo(
    () => [
      {
        title: 'Total Faturas',
        total: details?.invoices?.total_invoices,
        helpText:
          'Aqui você confere a quantidade de faturas geradas até o momento, pagas ou não. Vendas com mais de um item são consideradas em uma única fatura.',
      },
      {
        title: 'Pagas',
        total: details?.invoices?.paid?.total,
        percent: details?.invoices?.paid?.percentage,
        percentColor: '#158F55',
        helpText:
          'Essa é a quantidade de faturas com pagamento confirmado até o momento. Compras por meio de boletos podem demorar até 48h para confirmação.',
      },
      {
        title: 'Abertas',
        total: details?.invoices?.open?.total,
        percent: details?.invoices?.open?.percentage,
        percentColor: '#2009B3',
        helpText:
          'Essas são as faturas que ainda não tiveram o pagamento confirmado até o momento. Compras por meio de boletos podem demorar até 48h para confirmação e constarem como Pagas.',
      },
      {
        title: 'Em Recuperação',
        total: details?.invoices?.recovery?.total,
        percent: details?.invoices?.recovery?.percentage,
        percentColor: '#FFB661',
        helpText: (
          <p>
            Essas são as Faturas Abertas que estão em processo de recuperação. Você sabia que temos um departamento só para isso?{' '}
            <a
              href='https://ajuda.eduzz.com/hc/pt-br/articles/1260807625289-Como-funciona-a-recupera%C3%A7%C3%A3o-de-vendas-perdidas-RVP-'
              target='_blank'
              rel='noopener noreferrer'
              style={{ color: '#fff', textDecoration: 'underline' }}
            >
              Saiba mais
            </a>{' '}
            sobre o recurso de Recuperação de Vendas Perdidas (RVP).
          </p>
        ),
      },
      {
        title: 'Reembolsadas',
        total: details?.invoices?.refund?.total,
        percent: details?.invoices?.refund?.percentage,
        percentColor: '#dc3f53',
        helpText:
          'Aqui você acompanha o número de faturas que já foram Reembolsadas, ou que estão Aguardando Reembolso. Reembolsos Parciais não alteram o status das faturas e não são considerados.',
      },
      {
        title: 'Canceladas',
        total: details?.invoices?.canceled?.total,
        percent: details?.invoices?.canceled?.percentage,
        helpText:
          'Quando um comprador com Fatura Em Recuperação recusa as ofertas do nosso time de Recuperação de Vendas Perdidas (RVP), a fatura recebe o status de Cancelada.',
      },
      {
        title: 'Expiradas',
        total: details?.invoices?.expired?.total,
        percent: details?.invoices?.expired?.percentage,
        helpText:
          'São as faturas de compradores que geraram um boleto mas, por desistência ou por outros motivos, não realizam o pagamento dentro do período de vencimento.',
      },
      {
        title: 'Duplicadas',
        total: details?.invoices?.duplicated?.total,
        percent: details?.invoices?.duplicated?.percentage,
        helpText: (
          <p>
            Quando um comprador tenta pagar mais de uma vez por um produto que já adquiriu em um curto espaço de tempo, esta fatura recebe o status de
            Duplicada. Precisa liberar esse tipo de compra?{' '}
            <a
              href='https://ajuda.eduzz.com/?article=como-adicionar-um-cliente-na-whitelist-orbita-2'
              target='_blank'
              rel='noopener noreferrer'
              style={{ color: '#fff', textDecoration: 'underline' }}
            >
              Conheça a Whitelist!
            </a>
          </p>
        ),
      },
    ],
    [details]
  );

  return (
    <div className={classes.root}>
      {!isLoadingDetails && hasDetailsError && <Error onRetry={handleRetry} />}
      {isLoadingDetails && <Spin />}
      {details && (
        <>
          <Typography marginBottom>Receitas</Typography>
          <Grid container spacing={2}>
            {incomeCards.map((card) => (
              <Grid xs={12} sm={6} md={3} key={card.title} item>
                <ValueCard {...card} />
              </Grid>
            ))}
          </Grid>
          <div className='m8'></div>
          <Typography marginBottom>Faturas</Typography>
          <Grid container spacing={2}>
            {invoiceCards.map((card) => (
              <Grid xs={12} sm={6} md={3} key={card.title} item>
                <ValueCard {...card} />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </div>
  );
};

export default Statics;
